import styled from 'styled-components';

import CardGroupStyled from '../../../components/CardGroup';
import HeroStyled from '../../../components/Hero';
import LatestNewsStyled from '../../../components/LatestNews';
import Link from '../../../components/Link';
import PaginationStyled from '../../../components/Pagination';
import { ReactComponent as AccessibilitySVG } from '../../../images/icons/accessibility-56.svg';
import { ReactComponent as CategorySVG } from '../../../images/icons/category-pie-chart.svg';
import { ReactComponent as ChartMatchSVG } from '../../../images/icons/chart-match-56.svg';
import { ReactComponent as ColorPaletteSVG } from '../../../images/icons/color-palette-56.svg';
import { ReactComponent as ComponentsSVG } from '../../../images/icons/components-56.svg';
import { ReactComponent as DashboardTemplatesSVG } from '../../../images/icons/dashboard-templates-56.svg';
import { ReactComponent as IconographySVG } from '../../../images/icons/iconography-56.svg';
import { ReactComponent as LogosUsageSVG } from '../../../images/icons/logos-usage-56.svg';
import { ReactComponent as UIKitsSVG } from '../../../images/icons/ui-kits-56.svg';

const dataCardGroups = [
  {
    id: 1,
    title: 'Branding',
    content:
      'Our brand reflects who we are and how we want our users to feel when they use our products.',
    linkUrl: '/branding-styleguide',
    cards: [
      {
        title: 'Logos Usage',
        content: 'Our brand and product logos are clear, impactful, and recognizable.',
        linkUrl: '/branding-styleguide/logos',
        icon: <LogosUsageSVG />,
      },
      {
        title: 'Color Palette',
        content:
          'Color distinguishes our brand and reinforces consistent experiences across products.',
        linkUrl: '/branding-styleguide/colors',
        icon: <ColorPaletteSVG />,
      },
      {
        title: 'Iconography',
        content:
          'Icons are visual representations of commands, devices, directories, or common actions.',
        linkUrl: '/branding-styleguide/icons',
        icon: <IconographySVG />,
      },
    ],
  },
  {
    id: 2,
    title: 'Design',
    content:
      'Design principles serve as guiding notions that shape the design of the DHCS products.',
    linkUrl: '/general-components',
    cards: [
      {
        title: 'ChartMatch',
        content: 'Find the most appropriate chart for your project by taking a simple quiz.',
        linkUrl: '/design-system/chart-match',
        icon: <ChartMatchSVG />,
      },
      {
        title: 'Components',
        content: 'Components are the reusable building blocks of our design system.',
        linkUrl: '/general-components',
        icon: <ComponentsSVG />,
      },
      {
        title: 'Data Visualization',
        content:
          'Converting complex datasets into visual formats such as charts, graphs, and maps.',
        linkUrl: '/data-visualization',
        icon: <CategorySVG />,
      },
    ],
  },
  {
    id: 3,
    title: 'Development',
    content: 'Implement DHCS Design System with code and developer documentation.',
    linkUrl: '/dev-resources',
    cards: [
      {
        title: 'UI Kits',
        content: 'Rapidly build beautiful and accessible experiences with all resources you need.',
        linkUrl: '/dev-resources/figma-library',
        icon: <UIKitsSVG />,
      },
      {
        title: 'Dashboard Templates',
        content: 'Pre-made dashboards that will help you implement designs.',
        linkUrl: '/design-system/sample-dashboards',
        icon: <DashboardTemplatesSVG />,
      },

      {
        title: 'Accessibility',
        content:
          'Accessible design lets people of all abilities interact with, understand, and navigate our products.',
        linkUrl: '/get-started/accessibility',
        icon: <AccessibilitySVG />,
      },
    ],
  },
];

type GetStartedProps = {
  className?: string;
};

const GetStarted = ({ className = '' }: GetStartedProps) => (
  <div className={className}>
    <HeroStyled title="A Design System for a Human-Centered DHCS" />
    {dataCardGroups.map((cardGroup) => (
      <CardGroupStyled
        key={cardGroup.id}
        title={cardGroup.title}
        content={cardGroup.content}
        linkUrl={cardGroup.linkUrl}
        cards={cardGroup.cards}
      />
    ))}
    <LatestNewsStyled
      newsImage="images/news-image@3x.jpg"
      newsTitle="Design Evolved: Explore Our New Design System!"
      newsDate="02/09/24"
      newsContent="Introducing our brand new design system, meticulously crafted to elevate user experience to unprecedented heights. Drawing from the latest in design innovation and user feedback. Dive in and explore the next frontier of digital design."
    />
    <Link className="see-all-updates-link" href="/get-started/news">
      See all Updates
    </Link>
    <PaginationStyled
      nextLink="/branding-styleguide"
      next="Branding Styleguide"
      className="footer-pagination"
    />
  </div>
);

const StyledGetStarted = styled(GetStarted)`
  .see-all-updates-link {
    margin-top: 24px;
    margin-bottom: 72px;
  }
`;

export default StyledGetStarted;
